import React, { useContext, useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { Users, Calendar, Clock, Settings, LogOut, Home, FileText, Sliders } from 'lucide-react';
import api from '../../services/api';

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  let menuItems = [];

  if (user?.role === 'Super Admin') {
    menuItems = [
      { name: 'Employees', icon: Users, path: '/superadmin/employees' },
      { name: 'Team Attendance', icon: Clock, path: '/superadmin/attendance' },
      { name: 'Team Leaves', icon: Calendar, path: '/superadmin/leaves' },
      { name: 'Leave Balances', icon: Sliders, path: '/superadmin/leaves?tab=balances' },
      { name: 'Payroll', icon: FileText, path: '/superadmin/payroll' },
      { name: 'Settings', icon: Settings, path: '/superadmin/settings' },
    ];
    if (user?.isManager) menuItems.push({ name: 'My Team', icon: Users, path: '/employee/team-requests' });
  } else if (user?.role === 'Admin') {
    menuItems = [
      { name: 'Employees', icon: Users, path: '/admin/employees' },
      { name: 'Team Attendance', icon: Clock, path: '/admin/attendance' },
      { name: 'Team Leaves', icon: Calendar, path: '/admin/leaves' },
      { name: 'Leave Balances', icon: Sliders, path: '/admin/leaves?tab=balances' },
      { name: 'Payroll', icon: FileText, path: '/admin/payroll' },
    ];
    if (user?.isManager) menuItems.push({ name: 'My Team', icon: Users, path: '/employee/team-requests' });
  } else if (user?.role === 'HR') {
    menuItems = [
      { name: 'Employees', icon: Users, path: '/hr/employees' },
      { name: 'Team Attendance', icon: Clock, path: '/hr/attendance' },
      { name: 'Team Leaves', icon: Calendar, path: '/hr/leaves' },
      { name: 'Leave Balances', icon: Sliders, path: '/hr/leaves?tab=balances' },
      { name: 'Payroll', icon: FileText, path: '/hr/payroll' },
      { name: 'Settings', icon: Settings, path: '/hr/settings' },
      { name: 'My Profile', icon: Users, path: '/employee/profile' },
      { name: 'My Attendance', icon: Clock, path: '/employee/attendance' },
      { name: 'My Leaves', icon: Calendar, path: '/employee/leaves' },
      { name: 'Holidays', icon: Calendar, path: '/employee/holidays' },
      { name: 'My WFH & OD', icon: Home, path: '/employee/wfh' },
      { name: 'My Payslips', icon: FileText, path: '/employee/payslips' },
    ];
    if (user?.isManager) menuItems.push({ name: 'My Team', icon: Users, path: '/employee/team-requests' });
  } else {
    menuItems = [
      { name: 'Profile', icon: Users, path: '/employee/profile' },
      { name: 'My Attendance', icon: Clock, path: '/employee/attendance' },
      { name: 'My Leaves', icon: Calendar, path: '/employee/leaves' },
      { name: 'Holidays', icon: Calendar, path: '/employee/holidays' },
      { name: 'My WFH & OD', icon: Home, path: '/employee/wfh' },
      { name: 'My Payslips', icon: FileText, path: '/employee/payslips' },
    ];
    
    if (user?.isManager || user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Super Admin' || user?.role === 'HR') {
      menuItems.push({ name: 'My Team', icon: Users, path: '/employee/team-requests' });
    }
  }

  if (user?.workLocation === 'WFH') {
    menuItems = menuItems.filter(item => item.name !== 'My WFH & OD');
  }

  const [pendingLeaves, setPendingLeaves] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      if (['Admin', 'HR', 'Super Admin'].includes(user?.role)) {
        try {
          const leavesRes = await api.get('/leaves');
          setPendingLeaves(leavesRes.data.filter(l => l.status === 'PENDING').length);
        } catch (err) {}
      }
    };
    
    fetchCounts();
    const interval = setInterval(fetchCounts, 15000); // 15 seconds polling
    return () => clearInterval(interval);
  }, [user]);

  return (
    <>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 z-20 backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)} 
        />
      )}
      
      <div className={`bg-white border-r border-gray-100 flex flex-col justify-between h-full shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-30 fixed md:relative transition-all duration-300 ${isSidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full md:w-20 md:translate-x-0'}`}>
        <div className="flex-1 flex flex-col min-h-0">
        {/* Brand Area */}
        <div className={`h-16 flex items-center border-b border-gray-100 transition-all duration-300 shrink-0 ${isSidebarOpen ? 'px-4' : 'justify-center'}`}>
          {isSidebarOpen ? (
            <div className="flex items-center gap-3 overflow-hidden border border-white/20 rounded-md p-1 w-full bg-[#111]">
              <div className="bg-zinc-800 rounded shrink-0 w-8 h-8 flex items-center justify-center">
                <img src="/logo.png" alt="Logo" className="w-6 h-6 object-contain drop-shadow-md" />
              </div>
              <div className="flex items-center pr-2">
                <span className="whitespace-nowrap text-[17px] font-black tracking-wide text-white">CHALKS</span>
                <span className="whitespace-nowrap text-[17px] font-black tracking-wide text-[#e63946]">N</span>
                <span className="whitespace-nowrap text-[17px] font-black tracking-wide text-white">BOARD</span>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-800 rounded border border-white/20 shrink-0 w-10 h-10 flex items-center justify-center hidden md:flex cursor-pointer hover:bg-zinc-700 transition-colors" onClick={() => setIsSidebarOpen(true)}>
              <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain drop-shadow-md" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 mt-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent custom-scrollbar">
          {menuItems.map((item) => {
            const currentFull = location.pathname + location.search;
            const isActive = item.path.includes('?') 
              ? currentFull === item.path 
              : location.pathname === item.path && !location.search.includes('tab=balances');
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.path}
                onClick={() => {
                  if (window.innerWidth < 768) setIsSidebarOpen(false);
                }}
                title={!isSidebarOpen ? item.name : undefined}
                className={`relative flex items-center ${isSidebarOpen ? 'justify-between px-4' : 'justify-center px-0'} py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'bg-blue-50 text-blue-600 font-medium shadow-sm' 
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className={`flex items-center gap-3 overflow-hidden`}>
                  <Icon size={20} className={`shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600 transition-colors'}`} />
                  {isSidebarOpen && <span className="whitespace-nowrap transition-opacity duration-300">{item.name}</span>}
                </div>
                {item.name === 'Team Leaves' && pendingLeaves > 0 && isSidebarOpen && (
                  <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow-sm">
                    {pendingLeaves}
                  </span>
                )}
                {item.name === 'Team Leaves' && pendingLeaves > 0 && !isSidebarOpen && (
                  <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white shadow-sm"></span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Footer Area */}
      <div className="p-4 border-t border-gray-100 mb-2">
        <button
          onClick={logout}
          title={!isSidebarOpen ? 'Logout' : undefined}
          className={`w-full flex items-center ${isSidebarOpen ? 'gap-3 px-4' : 'justify-center px-4 md:px-0'} py-3 rounded-xl text-red-500 hover:bg-red-50 hover:text-red-600 transition-all duration-200 group font-medium focus:outline-none focus:ring-2 focus:ring-red-500/20`}
        >
          <LogOut size={22} className="shrink-0 text-red-400 group-hover:text-red-600 transition-colors" />
          <span className={`whitespace-nowrap ${!isSidebarOpen && 'md:hidden'}`}>Logout</span>
        </button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
